import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import cx from "classnames";
import _ from "lodash";
import styles from "./view.module.scss";
import Button from "src/components/button";

import { BannerView } from "src/components/banner";
import { LearningRecordCourse } from "src/types/coaching/learners";
import { CourseContext } from "./context";
import { AssessmentRow, LessonRow } from "./components/cards";
import { FileService } from "src/services/file.service";
import { PlaceholderCard } from "src/pages/coaching/manage/courses/edit/components/cards";

interface CourseSummaryComponentProps {
  course: LearningRecordCourse;
}

export const CourseSummaryComponent: React.FC<CourseSummaryComponentProps> = ({ course }) => {
  const courseContext = React.useContext(CourseContext);

  const name = useMemo(() => _.get(course, "course.name") || "", [course]);
  const status = useMemo(() => _.get(course, "status"), [course]);
  const description = useMemo(() => _.get(course, "course.description") || "", [course]);
  const banner = useMemo(() => _.get(course, "course.banner"), [course]);
  const skills = useMemo(() => _.get(course, "course.skills") || [], [course]);
  const contents = useMemo(() => course?.course?.contents || [], [course]);
  const passPercentage = useMemo(() => _.get(course, "course.passPercentage") || 0, [course]);

  const { lessonCount, assessmentCount } = contents.reduce(
    (acc, content: any) => {
      if (content.entityType === "lesson") acc.lessonCount++;
      if (content.entityType === "assessment") acc.assessmentCount++;
      return acc;
    },
    { lessonCount: 0, assessmentCount: 0 },
  );

  const getIndexForLesson = (lesson) => {
    const lessons = contents?.filter((content) => content.entityType === 'lesson');
    return _.findIndex(lessons, (content) => content.uuid === lesson);
  }

  const handleBackClicked = () => {
    if (courseContext.onBack) {
      courseContext.onBack();
    }
  }

  const handleContentClicked = (index) => {
    if (courseContext.onContentClicked) {
      courseContext.onContentClicked(index);
    }
  }

  const handleStartCourse = () => {
    if (courseContext.startCourse) {
      courseContext.startCourse();
    }
  }

  const handleEnrollCourse = () => {
    if (courseContext.enrollCourse) {
      courseContext.enrollCourse();
    }
  }

  const handleRetakeCourse = () => {
    if(courseContext.retakeCourse) {
      courseContext.retakeCourse();
    }
  }

  return (
    <div className={styles.courseContainer}>
      <div className={styles.left}>
        <div className={styles.courseDetails}>
          {courseContext.onBack && (
            <div className={styles.back} onClick={handleBackClicked}>
              <i className="paperflite-left-arrow"></i>
              <span>my courses</span>
            </div>
          )}

          <div className={styles.details}>
            <div className={styles.name}>{name}</div>
            <div className={styles.thumbnail}>
              <BannerView
                banner={banner as any}
                defaultBanner="/images/assets/default_asset.jpg"
              />

              {status === "NOT_ENROLLED" && (
                <Button
                  className={styles.startCourse}
                  text={courseContext.enrollingCourse ? "Enrolling..." : "Enroll Course"}
                  variant="gradient"
                  icon="paperflite-play"
                  disabled={courseContext.enrollingCourse}
                  onClick={handleEnrollCourse}
                />
              )}

              {status === "NOT_STARTED" && (
                <Button
                  className={styles.startCourse}
                  text={courseContext.startingCourse ? "Starting..." : "Start Course"}
                  variant="gradient"
                  icon="paperflite-play"
                  disabled={courseContext.startingCourse}
                  onClick={handleStartCourse}
                />
              )}

              {status === "IN_PROGRESS" && (
                <Button
                  className={styles.startCourse}
                  text="Resume Course"
                  variant="gradient"
                  icon="paperflite-play"
                  onClick={() => {
                    if (courseContext.onContentClicked) {
                      courseContext.onContentClicked(0);
                    }
                  }}
                />
              )}

              {status === "COMPLETED"  && (
                <Button
                  className={styles.startCourse}
                  text={courseContext.retakingCourse ? "Retaking..." : "Retake Course"}
                  variant="gradient"
                  icon="paperflite-play"
                  disabled={courseContext.retakingCourse}
                  onClick={handleRetakeCourse}
                />
              )}

            </div>

            <div className={styles.courseMetadata}>
              <div className={styles.metadata}>
                <i className="paperflite-file" />
                {lessonCount} {lessonCount < 2 ? "lesson" : "lessons"}
              </div>
              <span className={styles.border} />
              <div className={styles.metadata}>
                <i className="paperflite-file" />
                {assessmentCount}{" "}
                {assessmentCount < 2 ? "assessment" : "assessments"}
              </div>
              <span className={styles.border} />
              <div className={styles.metadata}>
                <i className="paperflite-clock" />
                {FileService.formatDuration(course?.course?.duration || 0, 'long')}
              </div>
            </div>

            <div className={styles.description}>
              <p className={styles.title}>Description</p>
              <div
                className={styles.descriptionContent}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description),
                }}
              />
            </div>

            <div className={styles.content}>
              <div className={styles.header}>
                <p className={styles.title}>Course Structure</p>
              </div>
              <div className={styles.contentsList}>
                {contents.map((content, index) => {
                  const type = _.get(content, "entityType");
                  if (type === "lesson") {
                    const lessonIndex = getIndexForLesson(content.uuid);
                    return <LessonRow key={index}
                      className={styles.contentCard}
                      lessonIndex={lessonIndex}
                      course={course}
                      lesson={content}
                      index={index}
                      active={false}
                      onClick={handleContentClicked}
                    />
                  } else if (type === 'assessment') {
                    return <AssessmentRow key={index}
                      className={styles.contentCard}
                      course={course}
                      assessment={content}
                      index={index}
                      active={false}
                      onClick={handleContentClicked}
                    />
                  }
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.right}>
        <p>Skills you will get</p>
        <div className={styles.skills}>
          {_.size(skills) === 0 && (
            <div className={styles.empty}>No skills available</div>
          )}

          {skills.map((skill: any) => {
            return (
              <div key={skill.id} className={styles.skill}>
                <div className={styles.name}>{skill.name}</div>
              </div>
            );
          })}
        </div>
        <div className={styles.passPercentage}>
          <p className={styles.heading}>Pass percentage to get:</p>
          <p className={styles.percentage}>{passPercentage}%</p>
          <p className={styles.description}>
            This represents the minimum pass percentage that you must
            achieve for this course.
          </p>
        </div>
      </div>
    </div>
  );
};

export const LoadingCoursePage: React.FC<object> = () => {
  return (
    <div className={cx(styles.loadingContainer, styles.placeholder)}>
      <div className={cx(styles.name, styles.block)}></div>
      <div className={cx(styles.thumbnail, styles.block)}></div>

      <div className={cx(styles.description)}>
        <div className={cx(styles.block, styles.label)}></div>
        <div className={cx(styles.block, styles.input)}></div>
        <div className={cx(styles.block, styles.input)}></div>
      </div>

      <div className={styles.content}>
        <div className={cx(styles.header, styles.block)}></div>
        <div className={styles.contentsList}>
          <PlaceholderCard index={0} />
        </div>
      </div>
    </div>)
}
